"use client";
import Image from "next/image";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useState, useEffect, useRef, useCallback } from "react";
import { MoveRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { WorkLightbox } from "./WorkLightbox";

const images = [
  // "/img/my_work/Baum Ju Garden.jpg",
  "/img/my_work/Hecke und Rasen sauber geschnitten.jpeg",
  "/img/my_work/Geschnittene Hecke mit tool.jpeg",
  "/img/my_work/Geschnittene Hecke.jpeg",
  "/img/my_work/Heckenschnitt 4.jpg",
  "/img/my_work/Heckenschnitt Ju Garden.jpg",
  "/img/my_work/Heckenschnitt.jpeg",
  "/img/my_work/hecke-geschnitten.jpeg",
  "/img/my_work/vorher-nacher-unkraut.jpeg",
  "/img/my_work/entwurzel-mit-werkzeug.jpeg",
  "/img/my_work/entwurzel-mit-werkzeug2.jpeg",
  "/img/my_work/Stubbenfräse.jpeg",
];

export function WorkCarousel() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomedIndex, setZoomedIndex] = useState<number | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!carouselApi) return;

    const isDesktop = () => window.matchMedia("(min-width: 768px)").matches;

    const startAutoScroll = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const delay = isDesktop() ? 6000 : 3000;
      const steps = isDesktop() ? 3 : 1;
      intervalRef.current = setInterval(() => {
        const total = carouselApi.scrollSnapList().length;
        const current = carouselApi.selectedScrollSnap();
        const next = (current + steps) % total;
        carouselApi.scrollTo(next);
      }, delay);
    };

    startAutoScroll();

    const mq = window.matchMedia("(min-width: 768px)");
    mq.addEventListener("change", startAutoScroll);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      mq.removeEventListener("change", startAutoScroll);
    };
  }, [carouselApi]);

  const isDesktopCheck = useCallback(
    () => window.matchMedia("(min-width: 768px)").matches,
    [],
  );

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  };

  const handleImageClick = (index: number) => {
    if (isDesktopCheck()) {
      setZoomedIndex((prev) => (prev === index ? null : index));
    } else {
      openLightbox(index);
    }
  };

  const closeZoom = useCallback(() => {
    setZoomedIndex(null);
  }, []);

  // Tastatur-Navigation im Zoom
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (zoomedIndex === null) return;
      if (e.key === "Escape") closeZoom();
      if (e.key === "ArrowLeft")
        setZoomedIndex((prev) =>
          prev !== null ? (prev - 1 + images.length) % images.length : 0,
        );
      if (e.key === "ArrowRight")
        setZoomedIndex((prev) =>
          prev !== null ? (prev + 1) % images.length : 0,
        );
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [zoomedIndex, closeZoom]);

  return (
    <section className="py-24 bg-green-950 text-primary-foreground relative overflow-hidden mx-auto">
      {/* Dekoratives Hintergrund-Element für mehr Tiefe */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.1),transparent)] pointer-events-none" />

      {/* Fullscreen-Zoom Overlay für Desktop */}
      {zoomedIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm cursor-pointer"
          onClick={closeZoom}
        >
          {/* Schließen */}
          <button
            onClick={closeZoom}
            className="absolute top-4 right-4 z-50 p-2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full transition-colors cursor-pointer"
            aria-label="Schließen"
          >
            <X className="w-7 h-7" />
          </button>

          {/* Zurück */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setZoomedIndex((prev) =>
                prev !== null ? (prev - 1 + images.length) % images.length : 0,
              );
            }}
            className="absolute left-3 md:left-8 z-50 p-2 md:p-3 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full transition-colors cursor-pointer"
            aria-label="Vorheriges Bild"
          >
            <ChevronLeft className="w-7 h-7 md:w-9 md:h-9" />
          </button>

          {/* Bild */}
          <div
            className="relative w-[85vw] h-[85vh] max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Image
              src={images[zoomedIndex]}
              alt={`Arbeitsbeispiel ${zoomedIndex + 1}`}
              fill
              className="object-contain rounded-2xl"
              sizes="85vw"
              priority
            />
          </div>

          {/* Weiter */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setZoomedIndex((prev) =>
                prev !== null ? (prev + 1) % images.length : 0,
              );
            }}
            className="absolute right-3 md:right-8 z-50 p-2 md:p-3 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full transition-colors cursor-pointer"
            aria-label="Nächstes Bild"
          >
            <ChevronRight className="w-7 h-7 md:w-9 md:h-9" />
          </button>
        </div>
      )}

      <div className="container px-4 md:px-6 relative z-10 mx-auto">
        <div className="text-center mb-12 space-y-2">
          <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl font-serif">
            Unsere Arbeit
          </h2>
          <p className="text-primary-foreground/80 text-lg max-w-150 mx-auto bg-linear-to-r from-primary-foreground/60 via-primary-foreground/80 to-primary-foreground/60 bg-clip-text">
            Bilder sagen mehr als tausend Worte. Hier ein kleiner Einblick in
            unsere Projekte.
          </p>
        </div>

        <div className="flex justify-center px-2 md:px-10 lg:px-16">
          <Carousel
            className="w-full max-w-xs md:max-w-4xl"
            setApi={setCarouselApi}
            opts={{
              align: "start",
              loop: true,
            }}
          >
            <CarouselContent className="-ml-4">
              {images.map((src, index) => (
                <CarouselItem
                  key={index}
                  className="pl-4 md:basis-1/2 lg:basis-1/3"
                >
                  <div className="p-1">
                    <div
                      className="overflow-hidden rounded-2xl shadow-xl border-4 border-white/20 hover:border-white/40 transition-all duration-300 group bg-black/5 cursor-pointer"
                      onClick={() => handleImageClick(index)}
                    >
                      <div className="relative aspect-square">
                        <Image
                          src={src}
                          alt={`Arbeitsbeispiel ${index + 1}`}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        {/* Overlay effekt beim Hover */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white font-medium bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
                            Vergrößern
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="cursor-pointer hidden md:inline-flex -left-9 lg:-left-14 bg-white/20 hover:bg-white/40 border-none text-white h-8 w-8 lg:h-12 lg:w-12" />
            <CarouselNext className="cursor-pointer hidden md:inline-flex -right-9 lg:-right-14 bg-white/20 hover:bg-white/40 border-none text-white h-8 w-8 lg:h-12 lg:w-12" />
          </Carousel>
        </div>

        {/* Swipe-Hinweis nur auf Mobile */}
        <div className="flex md:hidden items-center justify-center gap-2 mt-4 text-primary-foreground/60 text-sm animate-pulse">
          <span>Wischen zum Blättern</span>
          <MoveRight className="w-4 h-4" />
        </div>
      </div>

      <WorkLightbox
        images={images}
        initialIndex={currentImageIndex}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
      />
    </section>
  );
}
